/* eslint-disable no-nested-ternary */
import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment-timezone';
import { useTranslation } from 'react-i18next';

import { humanize } from 'common/helpers';
import NoteSvgNew from 'common/images/NoteSvgNew';
import { getWaveformDurationMs } from 'utils/overlayTraceUtils';
import { formatSpeedDisplay } from 'utils/overlayLegendUtils';
import {
  LegendItem,
  ColorIndicator,
  CloseIcon,
  EditIcon,
  DescriptionInput,
  LegendHeader,
  LegendMain,
  LegendContent,
  LegendDate,
  LegendInfo,
  SpeedValue,
  SpeedBadge,
  SpeedLabel,
  ActionButtons,
  DescriptionText
} from './OverlayTraceLegend.styled';

const LegendItemRow = ({
  itemData,
  isTraceOverlay,
  showOverlay,
  selectedPoints,
  timezone,
  type,
  yUnit,
  chartData,
  traceDataKey,
  shaftSpeedUnit,
  config,
  hoveredLegendItem,
  activeLegendItem,
  editingDescriptionIndex,
  tempDescription,
  retryOverlayTrace,
  onLegendItemHover,
  onLegendItemLeave,
  onLegendItemClick,
  onEditDescription,
  onRemoveLegendItem,
  onDescriptionChange,
  onSaveDescription,
  onDescriptionKeyDown,
  onRetryOverlayTrace
}) => {
  const { t } = useTranslation();
  const { index, pointToShow, speedValue, color, tooltip, traceStatusText } = itemData;
  const hasFocusedLegend = activeLegendItem !== null || hoveredLegendItem !== null;
  const isFocused = hoveredLegendItem === index || activeLegendItem === index;

  const formatSpeed = speed => formatSpeedDisplay({
    speedValue: speed,
    shaftSpeedUnit,
    config
  });

  const getTraceStatusError = (point) => {
    if (!point || !point.traceStatus) return false;
    const traceType = traceDataKey === 'waveform_data' ? 'waveform' : 'spectrum';
    return point.traceStatus[traceType] === 'error';
  };

  const renderTraceMetadata = (point) => {
    if (!point || traceDataKey !== 'waveform_data') return null;
    const durationMs = getWaveformDurationMs(point);
    const parts = [];
    if (point.sampling_rate) {
      parts.push(`${(point.sampling_rate / 1000).toFixed(1)} kHz`);
    }
    if (durationMs != null) {
      parts.push(`${durationMs.toFixed(1)} ms`);
    }
    if (!parts.length) return null;
    return parts.join(' • ');
  };

  const renderActionButtons = () => (
    <>
      <EditIcon
        isHovered={hoveredLegendItem === index}
        isActive={activeLegendItem === index}
        onClick={(e) => {
          e.stopPropagation();
          onEditDescription(index);
        }}
        title={t('machineDashboard.health.charts.toolbar.editDescription')}
        data-action="edit"
      >
        <NoteSvgNew width={10} height={10} fill="#444" />
      </EditIcon>
      <CloseIcon
        isHovered={hoveredLegendItem === index}
        isActive={activeLegendItem === index}
        onClick={(e) => {
          e.stopPropagation();
          onRemoveLegendItem(index, e);
        }}
        title={t('machineDashboard.health.charts.toolbar.removeSeries')}
        data-action="remove"
        className="close-icon"
      />
    </>
  );

  const renderDescriptionSection = (point) => {
    if (editingDescriptionIndex === index) {
      return (
        <DescriptionInput
          value={tempDescription}
          onChange={onDescriptionChange}
          onBlur={() => onSaveDescription(index)}
          onKeyDown={e => onDescriptionKeyDown(e, index)}
          autoFocus
          maxLength={50}
          type="text"
        />
      );
    }

    if (point && point.description) {
      return (
        <DescriptionText isEmpty={!point.description}>
          {point.description}
        </DescriptionText>
      );
    }

    return null;
  };

  const renderSpectrumOverlayContent = () => {
    const isPending = !!traceStatusText;
    const speedLabel = t('machineDashboard.health.charts.speedLabel', { defaultValue: 'Speed' });
    const traceMetadata = renderTraceMetadata(pointToShow);

    return (
      <>
        <LegendHeader>
          <LegendMain>
            <ColorIndicator color={color} style={isPending ? { opacity: 0.45 } : undefined} />
            <div style={{ minWidth: 0, flex: 1 }}>
              <LegendDate>
                {moment(pointToShow.x).tz(timezone).format('MM/DD HH:mm')}
              </LegendDate>
              {pointToShow && speedValue !== null && !traceStatusText && (
                <SpeedBadge>
                  <SpeedLabel>{speedLabel}</SpeedLabel>
                  <SpeedValue>{formatSpeed(speedValue)}</SpeedValue>
                </SpeedBadge>
              )}
            </div>
          </LegendMain>
          <ActionButtons>
            {renderActionButtons()}
          </ActionButtons>
        </LegendHeader>

        {(renderDescriptionSection(pointToShow) || traceStatusText || traceMetadata) && (
          <LegendContent>
            {renderDescriptionSection(pointToShow)}
            {traceStatusText && (
              <LegendInfo style={{ fontStyle: 'italic' }} aria-live="polite">
                {traceStatusText}
                {getTraceStatusError(pointToShow) && retryOverlayTrace && (
                  <button
                    type="button"
                    style={{
                      marginLeft: '6px',
                      border: 'none',
                      background: 'none',
                      color: '#4E89F4',
                      cursor: 'pointer',
                      padding: 0,
                      fontSize: 'inherit',
                      textDecoration: 'underline'
                    }}
                    onClick={e => onRetryOverlayTrace(pointToShow, e)}
                  >
                    {t('charts.overlay.retryMeasurement')}
                  </button>
                )}
              </LegendInfo>
            )}
            {traceMetadata && (
              <LegendInfo>{traceMetadata}</LegendInfo>
            )}
          </LegendContent>
        )}
      </>
    );
  };

  const renderOverlayContent = () => {
    const speedLabel = t('machineDashboard.health.charts.speedLabel', { defaultValue: 'Speed' });

    return (
      <>
        <LegendHeader>
          <LegendMain>
            <ColorIndicator color={color} />
            <div style={{ minWidth: 0, flex: 1 }}>
              <LegendDate>
                {moment(selectedPoints[index].x).tz(timezone).format('MM/DD HH:mm')}
              </LegendDate>
              {speedValue !== null && (
                <SpeedBadge>
                  <SpeedLabel>{speedLabel}</SpeedLabel>
                  <SpeedValue>{formatSpeed(speedValue)}</SpeedValue>
                </SpeedBadge>
              )}
            </div>
          </LegendMain>
          <ActionButtons>
            {renderActionButtons()}
          </ActionButtons>
        </LegendHeader>

        {renderDescriptionSection(selectedPoints[index]) && (
          <LegendContent>
            {renderDescriptionSection(selectedPoints[index])}
          </LegendContent>
        )}
      </>
    );
  };

  const renderDefaultContent = () => (
    <span style={{ display: 'flex', alignItems: 'center' }}>
      <ColorIndicator color={color} />
      {type === 'velocity' || type === 'acceleration'
        ? `${humanize(type)} ${index + 1}`
        : `Measurement ${index + 1}`
      }
      {chartData[index] && chartData[index][0] && chartData[index][0].y !== undefined && !itemData.isTraceOverlay && !showOverlay &&
        ` (${(chartData[index][0].y).toFixed(2)}${yUnit ? ` ${yUnit}` : ''})`
      }
    </span>
  );

  const renderContent = () => {
    if (isTraceOverlay && pointToShow) {
      return renderSpectrumOverlayContent();
    }
    if (showOverlay && selectedPoints && selectedPoints.length > 0 && index < selectedPoints.length && selectedPoints[index].x) {
      return renderOverlayContent();
    }
    return renderDefaultContent();
  };

  return (
    <LegendItem
      key={index}
      multiLine={isTraceOverlay}
      traceColor={color}
      isHovered={hoveredLegendItem === index}
      isActive={activeLegendItem === index}
      dimmed={hasFocusedLegend && !isFocused}
      role="button"
      tabIndex={0}
      aria-pressed={activeLegendItem === index}
      onMouseEnter={() => onLegendItemHover(index)}
      onMouseLeave={onLegendItemLeave}
      onClick={e => onLegendItemClick(index, e)}
      onKeyDown={(e) => {
        if (e.target !== e.currentTarget) return;
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onLegendItemClick(index, e);
        }
      }}
      title={tooltip}
    >
      {renderContent()}
    </LegendItem>
  );
};

LegendItemRow.propTypes = {
  itemData: PropTypes.object.isRequired,
  isTraceOverlay: PropTypes.bool,
  showOverlay: PropTypes.bool,
  selectedPoints: PropTypes.array,
  timezone: PropTypes.string.isRequired,
  type: PropTypes.string,
  yUnit: PropTypes.string,
  chartData: PropTypes.array,
  traceDataKey: PropTypes.string,
  shaftSpeedUnit: PropTypes.string,
  config: PropTypes.object,
  hoveredLegendItem: PropTypes.number,
  activeLegendItem: PropTypes.number,
  editingDescriptionIndex: PropTypes.number,
  tempDescription: PropTypes.string,
  retryOverlayTrace: PropTypes.func,
  onLegendItemHover: PropTypes.func.isRequired,
  onLegendItemLeave: PropTypes.func.isRequired,
  onLegendItemClick: PropTypes.func.isRequired,
  onEditDescription: PropTypes.func.isRequired,
  onRemoveLegendItem: PropTypes.func.isRequired,
  onDescriptionChange: PropTypes.func.isRequired,
  onSaveDescription: PropTypes.func.isRequired,
  onDescriptionKeyDown: PropTypes.func.isRequired,
  onRetryOverlayTrace: PropTypes.func.isRequired
};

LegendItemRow.defaultProps = {
  isTraceOverlay: false,
  showOverlay: false,
  selectedPoints: [],
  type: null,
  yUnit: null,
  chartData: [],
  traceDataKey: null,
  shaftSpeedUnit: null,
  config: {},
  hoveredLegendItem: null,
  activeLegendItem: null,
  editingDescriptionIndex: null,
  tempDescription: '',
  retryOverlayTrace: null
};

export default LegendItemRow;
